import React from "react";

import { If } from "../shared/components/control-statements";
import { useIsMobile } from "../shared/hooks/useIsMobile";

import { Empty } from "../shared/components/Empty";
import { Loading } from "../shared/components/Loading";
import { Title } from "../shared/components/Title";

import {
  CashbackProgramFormDrawer,
  CashbackProgramHistoryDrawer,
  CashbackProgramIndicators,
  CashbackProgramResume,
} from "../shared/containers/cashback-program";

import { Drawer } from "@components";
import { EllipsisVerticalIcon } from "shared/components/Icons";
import { UserStorage } from "shared/tools/UserStorage";
import { CashbackProgramAPI } from "../providers/CashbackProgramAPI";
import { Button } from "../shared/components/Button";
import { Icon } from "../shared/components/core/Icon";
import CashbackOperator from "../shared/containers/cashback-program/CashbackOperator";
import { EnvironmentStorage } from "../shared/tools/EnvironmentStorage";
import { Dates } from "../shared/utils/Dates";
import style from "../styles/CashbackProgram.module.scss";
import LimitOperationsCashback from "./LimitOperationsCashback";
import { Select } from "@components";
import { OlgaEnvironmentsAPI } from "providers/OlgaEnvironmentsAPI";

class CashbackProgramPage extends React.Component {
  state = {
    date: [],
    hideDate: true,
    isLoading: true,
    selectedProgram: null,
    program: null,
    brands: [],
    brandId: null,
    showHistoryButton: true,
    programReports: {},
    programLabels: null,
    showHistoryDrawer: false,
    showFormDrawer: false,
    showOperatorDrawer: false,
    hasError: false,
    openOptions: false,
    openLimitOperationsCashback: false,
  };

  async setAsyncState(data) {
    return await new Promise((resolve) => this.setState(data, resolve));
  }

  componentDidMount() {
    this.loadCashbackProgram();
    this.loadBrands();
    this.loadProgramLabels();
  }

  async loadCashbackProgram(id = null) {
    const { brandId } = this.state;

    await this.setAsyncState({ isLoading: true });
    const response = await CashbackProgramAPI.getProgram(id, brandId);
    const program = response?.getData(null) || null;

    if (!program) {
      await this.setAsyncState({
        selectedProgram: null,
        program: null,
        isLoading: false,
      });
      return;
    }

    this.setState({
      isLoading: false,
      selectedProgram: program.id,
      program: program,
      showHistoryButton: id == null ? program.version_code > 1 : true,
    });

    await this.loadCashbackProgramReports(program.id);
  }

  async loadBrands() {
    const brandsResponse = await OlgaEnvironmentsAPI.getBrands();
    const brands = brandsResponse.getData([]);

    this.setState({
      brands,
    });
  }

  async loadProgramLabels () {
    const environmentConfigurationResponse = await OlgaEnvironmentsAPI.getConfigurations();
    const environmentConfiguration = environmentConfigurationResponse.getData({});
    const programLabels = environmentConfiguration?.program_labels || null;

    this.setState({
      programLabels,
    }); 
  }

  async loadCashbackProgramReports(id) {
    const response = await CashbackProgramAPI.getProgramReports(id);
    const reports = response.getData(null);
    if (response.getCode(500) != 200 || !reports) {
      this.setState({ isLoading: false });
    } else {
      this.setState({ programReports: reports, isLoading: false });
    }
  }

  async seePastProgram(program) {
    let programId = null;
    if (program.deleted_at) {
      this.setState({
        date: [
          Dates.toDateBRL(program.created_at),
          Dates.toDateBRL(program.deleted_at),
        ],
      });
      programId = program.id;
    }

    await this.setAsyncState({ showHistoryDrawer: false });
    this.loadCashbackProgram(programId);
  }

  async toggleProgram() {
    const { program, selectedProgram } = this.state;
    const enabled = program.status != "ENABLED";

    const confirmed = await window.confirm({
      text: `Você tem certeza que deseja ${
        enabled ? "retomar" : "parar"
      } o programa?`,
      confirmText: enabled ? "Retomar" : "Parar",
      cancelText: "Cancelar",
    });

    if (!confirmed) {
      return false;
    }

    const response = await CashbackProgramAPI.putStatus(
      selectedProgram,
      enabled ? "ENABLED" : "DISABLED"
    );
    if (response.getCode(500) != 200) {
      alert({
        text: response.getErrors(["Ocorreu um erro ao salvar o programa!"])[0],
      });
      return;
    }

    await this.loadCashbackProgram();
  }

  getSubtitle = () => {
    const environment = EnvironmentStorage.getEnvironment();

    if (environment?.brand) return `${environment.brand.name}`;
    return environment?.name;
  };

  async selectBrand(brandId) {
    await this.setAsyncState({ brandId });
    await this.loadCashbackProgram();
  }

  render() {
    const { isMobile } = this.props;
    const {
      date,
      program,
      isLoading,
      showFormDrawer,
      showOperatorDrawer,
      programReports,
      programLabels,
      selectedProgram,
      showHistoryButton,
      showHistoryDrawer,
      hasError,
      openOptions,
      openLimitOperationsCashback,
      brands,
      brandId,
    } = this.state;

    const brandOptions = brands.map(({ id, name }) => ({
      value: id,
      label: name,
    }));

    return hasError ? (
      <Empty>
        <Icon name="exclamation-circle" size="4x" light />
        <p>Ops!</p>
        <span>
          Ocorreu um erro ao carregar essa página. Verifique as configurações e
          tente novamente!
        </span>
      </Empty>
    ) : (
      <>
        <Title
          title="Programa de Fidelidade"
          date={date}
          backTo={{
            url: "/fidelizacao-e-marketing",
            name: "Fidelização e Marketing",
          }}
          onHideDates={() => {
            this.setState({ date: [] });
            this.loadCashbackProgram(null);
          }}
        >
          <If cond={brandOptions.length > 1}>
            <Select
              className={isMobile ? "reverse w-100 mb-15" : ""}
              value={brandId}
              options={[
                { value: null, label: "Programa principal" },
                ...brandOptions,
              ]}
              onChange={(e) => {
                const brandId = +e.target.value || null;
                this.selectBrand(brandId);
              }}
            />
          </If>
          <If cond={selectedProgram && !isLoading}>
            <If cond={!date || date.length == 0}>
              <Button
                className={isMobile ? "reverse w-100 mb-15" : "ml-10"}
                onClick={() => this.setState({ showFormDrawer: true })}
                text="Editar programa"
              />
              <Button
                className={isMobile ? "reverse w-100 mb-15" : "ml-10"}
                onClick={() => this.toggleProgram()}
                text={
                  program && program.status == "ENABLED"
                    ? "Parar programa"
                    : "Retomar programa"
                }
              />
            </If>
            <If cond={showHistoryButton}>
              <Button
                className={isMobile ? "reverse w-100 mb-15" : "ml-10"}
                onClick={() => this.setState({ showHistoryDrawer: true })}
                text="Ver passado"
              />
            </If>
            {/* <If cond={UserStorage.isGodMode}>
              <Button
                className="ml-10"
                onClick={() => this.setState({ openOptions: true })}
              >
                <EllipsisVerticalIcon />
              </Button>
            </If> */}
          </If>
        </Title>
        <If cond={isLoading}>
          <div className={style.loading}>
            <Loading />
          </div>
        </If>

        <If cond={!isLoading}>
          <If cond={!selectedProgram}>
            <Empty>
              <Icon name="fa-hand-holding-dollar" size="4x" light />
              <p>Não há programa de fidelidade</p>
              <Button
                design="primary"
                onClick={() => this.setState({ showFormDrawer: true })}
                className="mt-15"
              >
                Iniciar programa
              </Button>
            </Empty>
          </If>
          <If cond={selectedProgram}>
            <div className={style.cashback}>
              <CashbackProgramResume 
                cashback={program} 
                programLabels={programLabels}
              />
            </div>
          </If>

          <CashbackProgramHistoryDrawer
            visible={showHistoryDrawer}
            brandId={brandId}
            onSeeResults={this.seePastProgram.bind(this)}
            onClose={() => this.setState({ showHistoryDrawer: false })}
          />
          <CashbackProgramFormDrawer
            visible={showFormDrawer}
            brandId={brandId}
            programLabels={programLabels}
            onClose={() => this.setState({ showFormDrawer: false })}
            onSave={() => {
              this.loadCashbackProgram();
              this.setState({ showFormDrawer: false });
            }}
          />
          <CashbackOperator
            visible={showOperatorDrawer}
            brandId={brandId}
            onClose={() => this.setState({ showOperatorDrawer: false })}
          />
        </If>

        <Drawer
          visible={openOptions}
          onClose={() => this.setState({ openOptions: false })}
          title="Opções"
        >
          <div className={style.options}>
            <div
              className={style.option}
              onClick={() =>
                this.setState({ openLimitOperationsCashback: true })
              }
            >
              <div className={style.icon}>
                <Icon name="dollar-sign" size="lg" light />
              </div>
              <p>Limite de operações (Em breve)</p>
            </div>
          </div>
        </Drawer>

        <LimitOperationsCashback
          visible={openLimitOperationsCashback}
          onClose={() => this.setState({ openLimitOperationsCashback: false })}
        />
      </>
    );
  }
}

export default () => {
  const isMobile = useIsMobile();
  return <CashbackProgramPage isMobile={isMobile} />;
};
